import type { UiPreferences } from "./useUiPreferences";
import { defaultCheckInViewSettings } from "../checkin/checkinView";

// Darstellung hängt vom Bildschirm ab. Fachliche Filter, Vorauswahlen und Aktionen
// bleiben Benutzerpräferenzen; deshalb werden gemischte Bereiche feldweise getrennt.
const deviceFields: Partial<Record<keyof UiPreferences, true | readonly string[]>> = {
  menu: true,
  tableColumns: true,
  calendar: ["fillScreen", "availabilityFillScreen", "resourceOrderIds", "viewMode"],
  checkInPage: ["viewActive", "viewHistory", "cardSize"],
  checkInView: ["nameFormat", "showIconCheckedIn", "showIconLocked", "showIconBirthday", "showIconOverdue"],
};

export function splitUiPreferences(prefs: UiPreferences): { device: UiPreferences; user: UiPreferences } {
  const user: Record<string, unknown> = { ...prefs };
  const device: Record<string, unknown> = {};
  for (const [key, fields] of Object.entries(deviceFields)) {
    const value = user[key];
    if (value === undefined) continue;
    if (fields === true) {
      device[key] = value;
      delete user[key];
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      const remaining = { ...value } as Record<string, unknown>;
      const local: Record<string, unknown> = {};
      for (const field of fields) {
        if (field in remaining) {
          local[field] = remaining[field];
          delete remaining[field];
        }
      }
      if (Object.keys(local).length) device[key] = local;
      if (Object.keys(remaining).length) user[key] = remaining;
      else delete user[key];
    }
  }
  return { device: device as UiPreferences, user: user as UiPreferences };
}

export function combineUiPreferences(server: UiPreferences, local: UiPreferences): UiPreferences {
  const { user } = splitUiPreferences(server);
  const { device } = splitUiPreferences(local);
  const result: Record<string, unknown> = { ...user };
  for (const [key, value] of Object.entries(device)) {
    const fields = deviceFields[key as keyof UiPreferences];
    result[key] = fields === true ? value : { ...(result[key] as object ?? {}), ...value as object };
  }
  // Klappzustände lagen bis 14.10.2026 gerätelokal; ein Altwert gilt, bis das Konto eigene hat.
  if (result.collapsedFormSections === undefined && local.collapsedFormSections) result.collapsedFormSections = local.collapsedFormSections;
  if (result.checkInView) result.checkInView = { ...defaultCheckInViewSettings, ...result.checkInView as object };
  return result as UiPreferences;
}

// Konto-Scope verhindert, dass unterschiedliche Nutzer desselben Browsers
// gegenseitig ihre Ansicht verändern. Der Speicher verlässt dieses Gerät nie.
const storageKey = (accountId: string) => `m1member.ui.device.${accountId}`;

export function loadDeviceUiPreferences(accountId: string): UiPreferences {
  if (typeof window === "undefined") return {};
  const raw = window.localStorage.getItem(storageKey(accountId));
  if (!raw) return {};
  try {
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return splitUiPreferences(value as UiPreferences).device;
    }
  } catch (error) {
    console.error("Ungültige lokale Darstellungseinstellungen:", error);
  }
  return {};
}

export function saveDeviceUiPreferences(accountId: string, prefs: UiPreferences): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(accountId), JSON.stringify(splitUiPreferences(prefs).device));
}
